import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { FileSpreadsheet, Loader2, AlertTriangle, Download } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { applyAccountingProfile, type AccountingProfile, type AccountingPlanItem } from "./AccountingDetailsDialog";

const PAYMENT_CATEGORIES = ["full_payment", "got_scholarship", "private_scholarship", "full_scholarship", "exempted"];
const YN = ["Yes", "No"];
const YN_FIELDS = ["Have NHIF Card?", "Resides in Hostel?", "Have Uniforms?", "Have Medical Equipment?"] as const;

type Student = { id: string; student_number: string | null; first_name: string; last_name: string; class: string | null; current_semester?: string | null };

type PlannedRow = {
  rowNumber: number; // Excel row (header = 1)
  raw: Record<string, any>;
  student: Student;
  profile: AccountingProfile;
  toCreate: AccountingPlanItem[];
  toRemove: AccountingPlanItem[];
  errors: string[];
};

const HEADERS = [
  "Student #",
  "First Name",
  "Last Name",
  "Class",
  "Payment Category",
  "Have NHIF Card?",
  "Resides in Hostel?",
  "Have Uniforms?",
  "Have Medical Equipment?",
] as const;

const COL_WIDTHS = [16, 16, 16, 10, 24, 18, 20, 18, 24];

// Column letters matching HEADERS order
const PAYMENT_CAT_COL = "E";
const YN_COLS = ["F", "G", "H", "I"] as const;

function downloadBlob(buffer: ArrayBuffer, filename: string) {
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function buildWorkbook(
  sheetName: string,
  headers: readonly string[],
  rows: Array<Record<string, any>>,
  validations: Array<{ col: string; options: string[] }>,
  colWidths: number[],
) {
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(sheetName);
  ws.addRow(headers as string[]);
  ws.getRow(1).font = { bold: true };
  for (const r of rows) {
    ws.addRow(headers.map((h) => r[h] ?? ""));
  }
  colWidths.forEach((w, i) => {
    ws.getColumn(i + 1).width = w;
  });
  const last = rows.length + 1;
  for (const v of validations) {
    for (let r = 2; r <= last; r++) {
      ws.getCell(`${v.col}${r}`).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [`"${v.options.join(",")}"`],
        showErrorMessage: true,
        errorTitle: "Chaguo si halali",
        error: "Tafadhali chagua kutoka kwenye orodha.",
      };
    }
  }
  return await wb.xlsx.writeBuffer();
}

export function AccountingBulkExcel({
  students,
  totalFilteredCount,
  fetchAllFiltered,
}: {
  students: Student[];
  totalFilteredCount?: number;
  fetchAllFiltered?: () => Promise<Student[]>;
}) {
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [plannedRows, setPlannedRows] = useState<PlannedRow[]>([]);

  const totals = plannedRows.reduce(
    (acc, r) => ({
      students: acc.students + (r.errors.length ? 0 : 1),
      create: acc.create + r.toCreate.length,
      remove: acc.remove + r.toRemove.length,
      failed: acc.failed + (r.errors.length ? 1 : 0),
      createAmount: acc.createAmount + r.toCreate.reduce((s, i) => s + i.amount, 0),
      removeAmount: acc.removeAmount + r.toRemove.reduce((s, i) => s + i.amount, 0),
    }),
    { students: 0, create: 0, remove: 0, failed: 0, createAmount: 0, removeAmount: 0 },
  );

  const downloadTemplate = async () => {
    setBusy(true);
    try {
      const targetStudents = fetchAllFiltered ? await fetchAllFiltered() : students;
      const ids = targetStudents.map((s) => s.id);
      const { data: profiles } = await supabase
        .from("student_accounting_profile")
        .select("*")
        .in("student_id", ids);
      const pmap = new Map((profiles || []).map((p: any) => [p.student_id, p]));

      const rows = targetStudents.map((s) => {
        const p: any = pmap.get(s.id) || {};
        return {
          "Student #": s.student_number ?? "",
          "First Name": s.first_name,
          "Last Name": s.last_name,
          "Class": s.class ?? "",
          "Payment Category": p.payment_category ?? "",
          "Have NHIF Card?": p.has_nhif ? "Yes" : "No",
          "Resides in Hostel?": p.resides_hostel ? "Yes" : "No",
          "Have Uniforms?": p.has_uniform === undefined ? "Yes" : (p.has_uniform ? "Yes" : "No"),
          "Have Medical Equipment?": p.has_medical_equipment === undefined ? "Yes" : (p.has_medical_equipment ? "Yes" : "No"),
        };
      });

      const buf = await buildWorkbook(
        "Accounting",
        HEADERS,
        rows,
        [
          { col: PAYMENT_CAT_COL, options: PAYMENT_CATEGORIES },
          ...YN_COLS.map((col) => ({ col, options: YN })),
        ],
        COL_WIDTHS,
      );
      downloadBlob(buf, `accounting-details-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (e: any) {
      toast({ title: "Failed", description: e.message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const uploadFilled = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".xlsx,.xls";
    input.onchange = async (e: any) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setBusy(true);
      setPreviewing(true);
      try {
        const XLSX = await import("xlsx");
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows: any[] = XLSX.utils.sheet_to_json(ws, { defval: "" });

        const planned: PlannedRow[] = [];
        for (let i = 0; i < rows.length; i++) {
          const r = rows[i];
          const rowNumber = i + 2; // header on row 1
          const errors: string[] = [];

          const sNum = String(r["Student #"] || "").trim();
          const pcat = String(r["Payment Category"] || "").trim();
          const ynVals: Record<string, string> = {};
          for (const f of YN_FIELDS) {
            ynVals[f] = String(r[f] ?? "").trim();
            const v = ynVals[f].toLowerCase();
            if (v && v !== "yes" && v !== "no") {
              errors.push(`${f} lazima iwe Yes au No (imepatikana "${ynVals[f]}")`);
            }
          }
          if (!sNum) errors.push("Student # haijajazwa");
          if (pcat && !PAYMENT_CATEGORIES.includes(pcat)) {
            errors.push(`Payment Category si halali: "${pcat}"`);
          }

          // Skip totally empty rows
          if (!sNum && !pcat && YN_FIELDS.every((f) => !ynVals[f])) continue;

          let stu = students.find((s) => (s.student_number ?? "") === sNum);
          if (!stu && sNum) {
            const { data: srow } = await supabase
              .from("students")
              .select("id, student_number, first_name, last_name, class, current_semester")
              .eq("student_number", sNum)
              .maybeSingle();
            if (srow) stu = srow as Student;
            else errors.push("Mwanafunzi hajapatikana kwa Student # hii");
          }

          const placeholder: Student = {
            id: `row-${rowNumber}`,
            student_number: sNum || null,
            first_name: r["First Name"] ?? "?",
            last_name: r["Last Name"] ?? "?",
            class: r["Class"] ?? null,
          };
          const finalStudent = stu ?? placeholder;

          const profile: AccountingProfile = {
            payment_category: pcat || null,
            has_nhif: ynVals["Have NHIF Card?"].toLowerCase() === "yes",
            resides_hostel: ynVals["Resides in Hostel?"].toLowerCase() === "yes",
            has_uniform: (ynVals["Have Uniforms?"] || "Yes").toLowerCase() === "yes",
            has_medical_equipment: (ynVals["Have Medical Equipment?"] || "Yes").toLowerCase() === "yes",
          };

          let toCreate: AccountingPlanItem[] = [];
          let toRemove: AccountingPlanItem[] = [];
          if (stu && errors.length === 0) {
            try {
              const plan = await applyAccountingProfile(stu, profile, null, { dryRun: true });
              toCreate = plan.toCreate;
              toRemove = plan.toRemove;
              if (!stu.class) errors.push("Mwanafunzi hana darasa — billing haitatumika");
            } catch (err: any) {
              errors.push(err.message || "Imeshindikana kukokotoa plan");
            }
          }

          planned.push({ rowNumber, raw: r, student: finalStudent, profile, toCreate, toRemove, errors });
        }

        setPlannedRows(planned);
        setPreviewOpen(true);
      } catch (e: any) {
        toast({ title: "Failed", description: e.message, variant: "destructive" });
      } finally {
        setBusy(false);
        setPreviewing(false);
      }
    };
    input.click();
  };

  const confirmApply = async () => {
    setApplying(true);
    try {
      const { data: u } = await supabase.auth.getUser();
      const uid = u?.user?.id ?? null;

      let totalCreated = 0, totalRemoved = 0, processed = 0, failed = 0;
      for (const row of plannedRows) {
        if (row.errors.length) { failed++; continue; }
        try {
          const res = await applyAccountingProfile(row.student, row.profile, uid);
          totalCreated += res.created;
          totalRemoved += res.removed;
          processed++;
        } catch {
          failed++;
        }
      }
      toast({
        title: "Bulk update complete",
        description: `${processed} processed · ${totalCreated} invoices created · ${totalRemoved} removed · ${failed} failed.`,
      });
      setPreviewOpen(false);
      setPlannedRows([]);
    } finally {
      setApplying(false);
    }
  };

  const downloadErrorReport = async () => {
    const failed = plannedRows.filter((r) => r.errors.length);
    if (!failed.length) return;
    const ERR_HEADERS = ["Row", ...HEADERS, "Errors"] as const;
    const rows = failed.map((r) => ({
      "Row": r.rowNumber,
      "Student #": r.raw["Student #"] ?? r.student.student_number ?? "",
      "First Name": r.raw["First Name"] ?? r.student.first_name,
      "Last Name": r.raw["Last Name"] ?? r.student.last_name,
      "Class": r.raw["Class"] ?? r.student.class ?? "",
      "Payment Category": r.raw["Payment Category"] ?? "",
      "Have NHIF Card?": r.raw["Have NHIF Card?"] ?? "",
      "Resides in Hostel?": r.raw["Resides in Hostel?"] ?? "",
      "Have Uniforms?": r.raw["Have Uniforms?"] ?? "",
      "Have Medical Equipment?": r.raw["Have Medical Equipment?"] ?? "",
      "Errors": r.errors.join(" | "),
    }));
    // Headers shift right by 1 ("Row" first), so Payment Cat = F, YN = G..J
    const buf = await buildWorkbook(
      "Errors",
      ERR_HEADERS,
      rows,
      [
        { col: "F", options: PAYMENT_CATEGORIES },
        { col: "G", options: YN },
        { col: "H", options: YN },
        { col: "I", options: YN },
        { col: "J", options: YN },
      ],
      [6, ...COL_WIDTHS, 60],
    );
    downloadBlob(buf, `accounting-errors-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const fmt = (n: number) => n.toLocaleString();

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin sm:mr-2" /> : <FileSpreadsheet className="h-4 w-4 sm:mr-2" />}
            <span className="hidden sm:inline">Accounting Excel</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={downloadTemplate}>
            Download template ({totalFilteredCount ?? students.length})
          </DropdownMenuItem>
          <DropdownMenuItem onClick={uploadFilled}>Upload filled file</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={previewOpen} onOpenChange={(o) => !applying && setPreviewOpen(o)}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Hakiki mabadiliko ya Accounting</DialogTitle>
            <DialogDescription>
              Hakuna kitu kitakachoandikwa kwenye database mpaka uthibitishe.
            </DialogDescription>
          </DialogHeader>

          {previewing ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Inakokotoa…
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div className="rounded-md border p-2">
                  <div className="text-muted-foreground">Wanafunzi</div>
                  <div className="text-lg font-semibold">{fmt(totals.students)}</div>
                </div>
                <div className="rounded-md border p-2">
                  <div className="text-muted-foreground">Invoices mpya</div>
                  <div className="text-lg font-semibold text-emerald-600">+{fmt(totals.create)}</div>
                  <div className="text-[10px] text-muted-foreground">TSh {fmt(totals.createAmount)}</div>
                </div>
                <div className="rounded-md border p-2">
                  <div className="text-muted-foreground">Zitafutwa (unpaid)</div>
                  <div className="text-lg font-semibold text-destructive">−{fmt(totals.remove)}</div>
                  <div className="text-[10px] text-muted-foreground">TSh {fmt(totals.removeAmount)}</div>
                </div>
                <div className="rounded-md border p-2">
                  <div className="text-muted-foreground">Zinazokwama</div>
                  <div className="text-lg font-semibold text-amber-600">{fmt(totals.failed)}</div>
                </div>
              </div>

              <ScrollArea className="flex-1 mt-3 border rounded-md">
                <div className="divide-y">
                  {plannedRows.map((r, i) => (
                    <div key={i} className={`p-2 text-sm ${r.errors.length ? "bg-destructive/5" : ""}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium truncate">
                          <span className="text-xs text-muted-foreground mr-1">#{r.rowNumber}</span>
                          {r.student.first_name} {r.student.last_name}
                          <span className="ml-2 text-xs text-muted-foreground">
                            {r.student.student_number || "—"} · {r.student.class || "—"}
                          </span>
                        </div>
                        {!r.errors.length && r.toCreate.length === 0 && r.toRemove.length === 0 && (
                          <Badge variant="secondary">Hakuna mabadiliko</Badge>
                        )}
                      </div>
                      {r.errors.length > 0 && (
                        <ul className="mt-1 space-y-0.5">
                          {r.errors.map((er, j) => (
                            <li key={j} className="flex items-start gap-1 text-xs text-destructive">
                              <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                              <span>{er}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {(r.toCreate.length > 0 || r.toRemove.length > 0) && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {r.toCreate.map((it, j) => (
                            <Badge key={`c${j}`} className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                              +{it.name} ({fmt(it.amount)})
                            </Badge>
                          ))}
                          {r.toRemove.map((it, j) => (
                            <Badge key={`r${j}`} variant="outline" className="border-destructive text-destructive">
                              −{it.name} ({fmt(it.amount)})
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  {plannedRows.length === 0 && (
                    <div className="p-4 text-center text-muted-foreground text-sm">Hakuna safu zilizopatikana kwenye faili.</div>
                  )}
                </div>
              </ScrollArea>
            </>
          )}

          <DialogFooter className="gap-2 sm:gap-2 flex-wrap">
            {totals.failed > 0 && (
              <Button variant="outline" onClick={downloadErrorReport} disabled={applying} className="mr-auto">
                <Download className="h-4 w-4 mr-2" />
                Pakua makosa ({totals.failed})
              </Button>
            )}
            <Button variant="outline" onClick={() => setPreviewOpen(false)} disabled={applying}>Ghairi</Button>
            <Button onClick={confirmApply} disabled={applying || previewing || totals.create + totals.remove === 0}>
              {applying && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Thibitisha na Tekeleza
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
